/**
 * Every copy string a stand-alone chat's create, invitation, and members
 * surfaces render, verbatim from `.agents/design-prompts/52-standalone-chats.md`.
 * Locked. Do not paraphrase any of it, including the two lines marked below
 * as new rather than quoted.
 *
 * Every component and every test that renders one of these strings imports
 * it from here, so a copy change can never pass a test that hard-codes the
 * old string.
 */

export const NEW_CHAT_TITLE = "New chat";
export const NEW_CHAT_NAME_LABEL = "Name this chat";
export const NEW_CHAT_NAME_PLACEHOLDER = "Sunday Crew";
export const NEW_CHAT_ADD_PEOPLE_LABEL = "Add people";
export const NEW_CHAT_SUBMIT_LABEL = "Create chat";

/**
 * ISSUE-760. New strings, no quotation to draw from: the design brief
 * (`52-standalone-chats.md` line 48) named the search pattern to reuse, not
 * its copy. Plain, no exclamation, no apology, no dash characters, per the
 * `human-writing` rule.
 */
export const NEW_CHAT_ADD_PEOPLE_SEARCH_LABEL = "Add by username";
export const NEW_CHAT_ADD_PEOPLE_START_MESSAGE =
  "Type someone's full username to find them.";
export const NEW_CHAT_ADD_PEOPLE_SEARCHING_LABEL = "Searching";
export const NEW_CHAT_ADD_PEOPLE_NOT_FOUND_MESSAGE =
  "Nobody matched that username.";
export const NEW_CHAT_ADD_PEOPLE_ACTION_LABEL = "Add";
export const NEW_CHAT_ADD_PEOPLE_ADDED_LABEL = "Added";
export const NEW_CHAT_ADD_PEOPLE_REMOVE_LABEL = "Remove";
/**
 * ISSUE-945. This constant used to be `NEW_CHAT_SUBMIT_DISABLED_REASON` and
 * read "Add at least one person to create the chat.", which was the reason
 * `Create chat` sat disabled with only the creator in the draft.
 *
 * It was never true. `postChatCreateStandaloneHandler` drops the caller's own
 * id from `memberUserIds` before it does anything with the list, so a chat
 * with only its creator has always been creatable, and the refusal it mirrored
 * only ever checked the shape of the request. That refusal is gone, so the
 * line that explained it would now be a lie told to a fan who has a working
 * Create button in front of them.
 *
 * This replaces it rather than deleting it: the Add people section still needs
 * to say what happens if you skip it, because `Invite people` on the chat that
 * results is not visible from inside this dialog. New string, no quotation to
 * draw from. Plain, no exclamation, no apology, no dash characters, per the
 * `human-writing` rule.
 */
export const NEW_CHAT_ADD_PEOPLE_OPTIONAL_MESSAGE =
  "You can add people now, or invite them once the chat exists.";

/**
 * ISSUE-946. Not a new string: this is the sentence the New chat dialog
 * already rendered inline while a chat photo waited on a moderation result.
 * It moved here when the photo control became `ChatPhotoField`, so the
 * component takes it as a prop and the wording stays in the one file every
 * stand-alone chat surface reads its copy from.
 */
export const NEW_CHAT_PHOTO_PENDING_MESSAGE =
  "Your photo is being checked. The chat will use its initials until then.";

export const INVITATION_ACCEPT_LABEL = "Accept";
export const INVITATION_DECLINE_LABEL = "Decline";
export const INVITATION_DIALOG_TITLE = "Chat invitation";

export const CHAT_MEMBERS_TITLE = "Members";
export const MEMBER_REMOVE_LABEL = "Remove from chat";
export const MEMBER_MAKE_ADMIN_LABEL = "Make Admin";
export const MEMBER_BLOCK_LABEL = "Block";
export const MEMBER_REPORT_LABEL = "Report";

/**
 * ISSUE-786, owner answer `Q-786-A` in
 * `.agents/docs/decisions/2026-09-02-answers-from-the-owner.md`. The owner
 * pinned the title `Remove <name>?` and a two sentence body, and attached a
 * condition to the second sentence: it promises that a re-invited member gets
 * their view of the history back, and it ships only if that is true.
 *
 * It is not true. `postMessageListHandler` floors a native member's page at
 * `access.member.joinedAt`, and `addOrReactivateNativeMember` overwrites
 * `joinedAt` with the current time when a removed member re-accepts, so a
 * re-invited fan opens a blank timeline. `messageListJoinPoint.test.ts` locks
 * that in as intended behavior. Per the condition, the second sentence is cut
 * and the confirm ships with the title and the first sentence only.
 *
 * The confirm label reuses `MEMBER_REMOVE_LABEL` above, which is already the
 * real verb said out loud, so no new label string is minted here.
 */
export function buildMemberRemoveConfirmTitle(displayName: string): string {
  return `Remove ${displayName}?`;
}

export const MEMBER_REMOVE_CONFIRM_BODY =
  "They'll lose access to this chat and its history.";

export const CHAT_RENAME_LABEL = "Rename chat";
export const CHAT_CHANGE_PHOTO_LABEL = "Change photo";
export const CHAT_INVITE_PEOPLE_LABEL = "Invite to chat";

/**
 * ISSUE-283. New strings, not quotations: the design brief says `Invite
 * people` "reopens the add-people search from the create flow"
 * (`52-standalone-chats.md`, "Being the Admin" item 1), but a chat that
 * already exists sends the invitation the moment a fan taps it rather than
 * staging a list for a later `Create chat` submit, so its result row needs
 * its own action and completed-state labels, distinct from the create
 * flow's `Add`/`Added` in `NEW_CHAT_ADD_PEOPLE_ACTION_LABEL` above. Plain,
 * no exclamation, no apology, no dash characters, per the `human-writing`
 * rule.
 */
export const CHAT_INVITE_SEARCH_ACTION_LABEL = "Invite to chat";
export const CHAT_INVITE_SEARCH_SENT_LABEL = "Invited";
export const CHAT_INVITE_ALREADY_MEMBER_LABEL = "Already in this chat";

export const CHAT_DELETE_LABEL = "Delete chat";
export const CHAT_DELETE_CONFIRM_TITLE = "Delete this chat?";
export const CHAT_DELETE_CONFIRM_BODY =
  "Everyone loses access, and the whole history goes with it. This can't be undone.";

export const CHAT_LEAVE_LABEL = "Leave chat";
export const CHAT_LEAVE_CONFIRM_TITLE = "Leave this chat?";
export const CHAT_LEAVE_CONFIRM_BODY =
  "You'll stop seeing new messages, and you'll need a new invitation to rejoin.";

export const SOLE_ADMIN_TRANSFER_TITLE = "Make someone else Admin first";

/**
 * New string, not a quotation: the brief fixes the transfer prompt's title
 * only. Plain, no exclamation, no apology, no dash characters.
 */
export const SOLE_ADMIN_TRANSFER_BODY =
  "You're the only Admin here. Make someone else an Admin, then you can leave.";

/**
 * ISSUE-945. Shown in place of the transfer prompt when the sole Admin is
 * also the only member. New strings, no dash characters.
 */
export const ONLY_MEMBER_LEAVE_TITLE = "You're the only one in this chat";
export const ONLY_MEMBER_LEAVE_BODY =
  "Invite someone and make them an Admin before you leave, or delete the chat.";

/**
 * ISSUE-283. Covers a pool chat, a private chat and a stand-alone chat alike:
 * a stand-alone chat's empty state used to claim "Everyone in the pool sees
 * it", which is false once this card made the pane reachable outside a pool.
 * One true sentence for all three chat kinds beats a per-kind branch the
 * design brief pins no copy for.
 */
export const CHAT_EMPTY_STATE_BODY = "Say something. Everyone here sees it.";
